import {useEffect, useRef} from "react";
import {useSearchParams} from "react-router-dom";
import {useLocale, useMessages} from "i18n";
import {useHasMounted} from "utils/useHasMounted";
import {formatApiInstant} from "core/format/dateFormat";
import {formatMinor} from "./money";
import {findMyBookingRow, type MyBookingRow} from "./myBookings";
import {useMyBookings} from "./useMyBookings";
import {SponsorWithdrawal} from "./SponsorBookingWithdrawal";
import {ADVERTISING_TERMS_PATH} from "./sponsorRoutes";
import {
	EmptyNote,
	LoadingPanel,
	Panel,
	RequestError,
	Section,
	SectionHead,
	SectionLede,
	SectionTitle,
	TermsLink,
} from "./SponsorPage.styles";
import {
	Fact,
	Facts,
	FactTerm,
	FactValue,
	Headline,
	Impressions,
	List,
	PayButton,
	PayNote,
	PayRow,
	PayTerms,
	Rejection,
	RejectionLabel,
	Row,
	RowBody,
	RowHead,
	RowNote,
	RowStatus,
	StateChip,
	Thumbnail,
} from "./SponsorMyBookings.styles";

/**
 * The advertiser's own bookings, on the public `/advertise` page (epic #441).
 *
 * A dumb view over {@link useMyBookings} (epic #26): what a row means, when a rejection reason may be
 * shown and whether a booking may be paid for are all decided in `myBookings.ts`, which imports no
 * React. This file renders, scrolls, and does nothing else.
 *
 * It returns `null` for a signed-out visitor, so the page a stranger sees is exactly the page it was
 * before — the booking narrative, with nothing claiming anything about an account they do not have.
 *
 * ── The `?booking=` link ────────────────────────────────────────────────────────────────────────────
 * ONE parameter with one meaning: "show me this booking". Two journeys arrive on it. The approval
 * email, which asks its reader to come and pay, and Stripe's own return once they have, and both want
 * the same thing, which is to see that booking's real state. It used to render a full-page "Payment
 * received" panel INSTEAD of this list, which was a claim about money made purely from an unvalidated
 * query string: bookmarkable, repeatable, and true only by luck. The row says what the server says.
 *
 * The purchase-conversion arming that rides on the same parameter stays in {@link SponsorPage} and is
 * deliberately NOT moved here or gated on the booking's state. This section renders nothing at all for
 * a signed-out visitor and its list is read once, while a payment webhook can land seconds after
 * Stripe returns the browser, so a paid-state gate here would silently drop the conversion for every
 * advertiser whose webhook was slower than their redirect.
 */
export function SponsorMyBookings() {
	const m = useMessages();
	const copy = m.sponsor;
	const {locale} = useLocale();
	const {bookings, loading, error, visible, payingBookingId, payNow, markWithdrawn} = useMyBookings();
	const [searchParams] = useSearchParams();
	// `/advertise` is eagerly pre-rendered and the static artifact has no query string, so reading one
	// before mount would make the first client render diverge from the HTML it is hydrating (ui#74).
	const hasMounted = useHasMounted();
	const selectedBookingId = hasMounted ? searchParams.get("booking") : null;
	const selected = findMyBookingRow(bookings, selectedBookingId);
	const selectedRef = useRef<HTMLLIElement | null>(null);

	/*
	 * Bring the named booking into view once it is actually on screen. Keyed on the id rather than on
	 * the row, so a list re-read after a refused payment does not scroll the page out from under
	 * somebody who has since scrolled away themselves.
	 */
	useEffect(() => {
		selectedRef.current?.scrollIntoView({block: "center", behavior: "smooth"});
	}, [selectedBookingId, selected !== null]);

	if (!visible) {
		return null;
	}

	/** The one line under a row that says where the booking stands, or nothing when the chip says it all. */
	const noteFor = (booking: MyBookingRow): string | null => {
		if (booking.state === "PENDING_REVIEW") {
			return copy.mine.underReview;
		}
		if (booking.state === "EXPIRED") {
			return copy.mine.expired;
		}
		// Approved and unpaid, but the deadline has gone. The api's expiry sweep runs on a schedule, so
		// this row really is reachable, and it must not carry a button the api would refuse.
		if (booking.state === "AWAITING_PAYMENT" && !booking.payable) {
			return copy.mine.payWindowLapsed;
		}
		return null;
	};

	return (
		<Section>
			<SectionHead>
				<SectionTitle>{copy.mine.title}</SectionTitle>
				<SectionLede>{copy.mine.lede}</SectionLede>
			</SectionHead>
			{loading && <LoadingPanel>{m.common.loading}</LoadingPanel>}
			{!loading && error !== null && <RequestError role="alert">{copy.errors[error]}</RequestError>}
			{/* The list survives a REFUSED PAYMENT. That banner explains a row the reader can still see,
			    and blanking the list under it would hide the state the sentence is about. A failed LOAD
			    has no rows, and its empty state would read as "you have booked nothing", so that one
			    shows the banner alone. */}
			{!loading && (error === null || bookings.length > 0) && (
				<Panel>
					{bookings.length === 0 && <EmptyNote>{copy.mine.empty}</EmptyNote>}
					{bookings.length > 0 && (
						<List aria-label={copy.mine.listAria}>
							{bookings.map(booking => {
								const highlighted = booking.bookingId === selectedBookingId;
								const note = noteFor(booking);
								const paying = payingBookingId === booking.bookingId;
								return (
									<Row
										key={booking.bookingId}
										$highlighted={highlighted}
										ref={highlighted ? selectedRef : undefined}
									>
										{/* Decorative: it is the advertiser's own creative, and the headline beside
										    it already names the booking. */}
										{booking.coverImageUrl !== null
											&& <Thumbnail src={booking.coverImageUrl} alt=""/>}
										<RowBody>
											<RowHead>
												<Headline>{booking.headline}</Headline>
												<RowStatus>
													<Impressions>
														{copy.impressions(booking.impressions)}
													</Impressions>
													<StateChip $state={booking.state}>
														{copy.bookingStates[booking.state]}
													</StateChip>
												</RowStatus>
											</RowHead>
											<Facts>
												<Fact>
													<FactTerm>{copy.summary.dates}</FactTerm>
													<FactValue>{`${booking.startsOn} – ${booking.endsOn}`}</FactValue>
												</Fact>
												<Fact>
													<FactTerm>{copy.summary.length}</FactTerm>
													{/* Through the catalog, which is where the Romanian
													    `o zi` / `7 zile` / `120 de zile` rule lives. */}
													<FactValue>{copy.calendar.selection(booking.days)}</FactValue>
												</Fact>
												<Fact>
													<FactTerm>{copy.summary.total}</FactTerm>
													<FactValue>
														{formatMinor(booking.amountMinor, booking.currency, locale)}
													</FactValue>
												</Fact>
											</Facts>
											{booking.payable && (
												<>
													<PayRow>
														{/* The deadline is an api `LocalDateTime` in UTC, written in the
														    reader's own zone. A payment deadline three hours out is a
														    deadline somebody misses. */}
														<PayNote>
															{copy.mine.payBy(formatApiInstant(booking.payBy, locale) ?? "")}
														</PayNote>
														<PayButton
															disabled={paying}
															onClick={() => void payNow(booking.bookingId)}
														>
															{paying ? copy.mine.paying
																: copy.mine.payNow(formatMinor(booking.amountMinor, booking.currency, locale))}
														</PayButton>
													</PayRow>
													{/* Approval is our offer; completing payment accepts it. Keep the
													    terms linked beside the payment handoff (ui#2602). */}
													<PayTerms>
														{m.sponsorConsent.payUnderTerms(
															<TermsLink to={ADVERTISING_TERMS_PATH} target="_blank" rel="noopener">
																{copy.summary.termsLink}
															</TermsLink>,
															copy.mine.payNow(formatMinor(booking.amountMinor, booking.currency, locale)),
														)}
													</PayTerms>
												</>
											)}
											{booking.consumerWithdrawal && <SponsorWithdrawal booking={booking}
												contact={booking.consumerWithdrawal} onReceived={() => markWithdrawn(booking.bookingId)}/>}
											{note !== null && <RowNote>{note}</RowNote>}
											{booking.rejectionReason !== null && (
												<Rejection>
													<RejectionLabel>{copy.mine.rejection}</RejectionLabel>
													{/* Verbatim, never paraphrased: it is the whole explanation the
													    advertiser gets for a refused campaign. */}
													{booking.rejectionReason}
												</Rejection>
											)}
										</RowBody>
									</Row>
								);
							})}
						</List>
					)}
				</Panel>
			)}
		</Section>
	);
}
